import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import recetaMenuConfigService from '../../services/recetaMenuConfigService';
import ConfirmModal from '../../components/Common/ConfirmModal';
import Toast from '../../components/Common/Toast';

const TiemposComida = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [modalRowId, setModalRowId] = useState(null);
  const [modalNombre, setModalNombre] = useState('');
  const [modalActive, setModalActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await recetaMenuConfigService.getTiempos();
      if (res.success) {
        setRows(res.data || []);
      }
    } catch (e) {
      setToast({
        isOpen: true,
        message: e.response?.data?.message || 'Error al cargar tiempos',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setModalRowId(null);
    setModalNombre('');
    setModalActive(true);
    setModalMode('create');
  }, []);

  useEffect(() => {
    if (!modalOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeModal();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [modalOpen, closeModal]);

  useEffect(() => {
    if (modalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [modalOpen]);

  const openModalCreate = () => {
    setModalMode('create');
    setModalRowId(null);
    setModalNombre('');
    setModalActive(true);
    setModalOpen(true);
  };

  const openModalEdit = (row) => {
    setModalMode('edit');
    setModalRowId(row.id);
    setModalNombre(row.name);
    setModalActive(!!row.is_active);
    setModalOpen(true);
  };

  const submitModal = async (e) => {
    e.preventDefault();
    const t = modalNombre.trim();
    if (!t) {
      setToast({ isOpen: true, message: 'El nombre no puede estar vacío', type: 'error' });
      return;
    }
    setSaving(true);
    try {
      if (modalMode === 'create') {
        const res = await recetaMenuConfigService.createTiempo({ name: t, is_active: modalActive });
        if (res.success) {
          setToast({ isOpen: true, message: 'Tiempo creado', type: 'success' });
        }
      } else if (modalRowId) {
        const res = await recetaMenuConfigService.updateTiempo(modalRowId, { name: t, is_active: modalActive });
        if (res.success) {
          setToast({ isOpen: true, message: 'Cambios guardados', type: 'success' });
        }
      }
      await loadRows();
      closeModal();
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.errors || 'No se pudo guardar';
      const str = typeof msg === 'object' ? Object.values(msg).flat().join(' ') : String(msg);
      setToast({ isOpen: true, message: str, type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      const res = await recetaMenuConfigService.deleteTiempo(deleteTarget.id);
      if (res.success) {
        setToast({ isOpen: true, message: 'Tiempo eliminado', type: 'success' });
        setDeleteTarget(null);
        await loadRows();
      }
    } catch (e) {
      setToast({
        isOpen: true,
        message: e.response?.data?.message || 'Error al eliminar',
        type: 'error',
      });
    }
  };

  const modal = modalOpen
    ? createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <button
            type="button"
            className="absolute inset-0 bg-black/40 backdrop-blur-[1px]"
            aria-label="Cerrar"
            onClick={closeModal}
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-tiempos-title"
            className="relative w-full max-w-md rounded-xl bg-white shadow-xl border border-gray-200"
          >
            <div className="border-b border-gray-100 px-5 py-4">
              <h2 id="modal-tiempos-title" className="text-lg font-semibold text-gray-900">
                {modalMode === 'create' ? 'Nuevo tiempo' : 'Editar tiempo'}
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                {modalMode === 'create'
                  ? 'Nombre del tiempo y si estará activo en el menú.'
                  : 'Cambie el nombre o el estado del tiempo.'}
              </p>
            </div>
            <form onSubmit={submitModal} className="px-5 py-4 space-y-4">
              <div>
                <label htmlFor="modal-tiempo-nombre" className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre
                </label>
                <input
                  id="modal-tiempo-nombre"
                  type="text"
                  value={modalNombre}
                  onChange={(e) => setModalNombre(e.target.value)}
                  placeholder="Ej. Desayuno, colación, cena…"
                  autoFocus
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>
              <div>
                <span className="block text-sm font-medium text-gray-700 mb-2">Estado</span>
                <div className="flex flex-wrap gap-3">
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="modal-tiempo-estado"
                      checked={modalActive === true}
                      onChange={() => setModalActive(true)}
                      className="text-teal-600 focus:ring-teal-500"
                    />
                    <span className="text-sm text-gray-800">Activo</span>
                  </label>
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="modal-tiempo-estado"
                      checked={modalActive === false}
                      onChange={() => setModalActive(false)}
                      className="text-teal-600 focus:ring-teal-500"
                    />
                    <span className="text-sm text-gray-800">Inactivo</span>
                  </label>
                </div>
              </div>
              <div className="flex flex-wrap justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 rounded-md border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={!modalNombre.trim() || saving}
                  className="px-4 py-2 rounded-md bg-teal-600 hover:bg-teal-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-medium"
                >
                  {saving ? 'Guardando…' : modalMode === 'create' ? 'Agregar' : 'Guardar cambios'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )
    : null;

  if (loading) {
    return (
      <div className="p-6 max-w-3xl mx-auto flex justify-center items-center min-h-[12rem]">
        <p className="text-gray-600">Cargando tiempos…</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl mx-auto">
      {modal}

      <nav className="text-sm text-gray-600 mb-4">
        <Link to="/dashboard" className="hover:text-teal-600">
          Dashboard
        </Link>
        <span className="mx-2">/</span>
        <Link to="/produccion-concesionaria" className="hover:text-teal-600">
          Producción concesionaria
        </Link>
        <span className="mx-2">/</span>
        <span className="text-gray-800">Tiempos</span>
      </nav>

      <h1 className="text-2xl font-bold text-gray-800 mb-6">Tiempos</h1>

      <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
          <span className="text-sm font-medium text-gray-700">Tiempos de comida / menú</span>
          <button
            type="button"
            onClick={openModalCreate}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium transition-colors"
          >
            + Agregar tiempo
          </button>
        </div>
        {rows.length === 0 ? (
          <div className="p-8 text-center text-gray-500 text-sm">
            No hay tiempos. Pulse <strong className="text-gray-700">Agregar tiempo</strong> para crear uno.
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {rows.map((row) => (
              <div
                key={row.id}
                className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 hover:bg-gray-50/80"
              >
                <div className="flex-1 min-w-0">
                  <span className="font-semibold text-gray-900 text-base">{row.name}</span>
                </div>
                <div className="flex flex-wrap items-center gap-3 sm:justify-end">
                  <span
                    className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${
                      row.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {row.is_active ? 'Activo' : 'Inactivo'}
                  </span>
                  <button
                    type="button"
                    onClick={() => openModalEdit(row)}
                    className="text-sm text-teal-700 hover:text-teal-900 font-medium"
                  >
                    Editar
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleteTarget(row)}
                    className="text-sm text-red-600 hover:text-red-800 font-medium border-l border-gray-200 pl-3"
                  >
                    Eliminar
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-6 flex flex-wrap gap-4">
        <Link
          to="/produccion-concesionaria/configuraciones/tipo-plato"
          className="text-teal-700 text-sm font-medium hover:underline"
        >
          Tipo de plato →
        </Link>
        <Link
          to="/produccion/clasificacion-recetas/recetas"
          className="text-teal-700 text-sm font-medium hover:underline"
        >
          ← Recetas
        </Link>
      </div>

      <ConfirmModal
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        title="Eliminar tiempo"
        message={deleteTarget ? `¿Eliminar «${deleteTarget.name}»?` : ''}
        confirmText="Eliminar"
        cancelText="Cancelar"
        type="danger"
      />
      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, isOpen: false }))}
      />
    </div>
  );
};

export default TiemposComida;
